<template>
  <SearchModal :open="state.searchModalOpen"
               @close="state.searchModalOpen = false"
  />
  <FiltersModal :open="state.filtersModalOpen"
                @close="state.filtersModalOpen = false"
  />
  <div class="bottom-bar"
       data-test-id="bottom-bar"
       v-if="showBottomBar"
  >
    <div class="bottom-bar__item"
         data-test-id="search-button"
         @click="state.searchModalOpen = true"
    >
      <Icon name="search-line"
            class="bottom-bar__item-icon"
      />
      <div class="bottom-bar__item-title">
        {{ t('terms.search') }}
      </div>
      <div v-if="hasSearchQuery"
           class="bottom-bar__notification"
      >
        1
      </div>
    </div>
    <div class="bottom-bar__item"
         data-test-id="filters-button"
         @click="state.filtersModalOpen = true"
    >
      <Icon name="equalizer-line"
            class="bottom-bar__item-icon"
      />
      <div class="bottom-bar__item-title">
        {{ t('filters.filters') }}
      </div>
      <div v-if="activeFiltersAmount > 0"
           class="bottom-bar__notification"
      >
        {{ activeFiltersAmount }}
      </div>
    </div>
    <div class="bottom-bar__item"
         @click="gotoCheckout"
    >
      <Icon name="shopping-basket-2-line"
            class="bottom-bar__item-icon"
      />
      <div class="bottom-bar__item-title">
        {{ t('buttons.basket') }}
        <div v-if="basketItemsAmount > 0"
             class="bottom-bar__notification"
        >
          {{ basketItemsAmount }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue'

import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import Icon from '@/components/partials/Icon.vue'
import SearchModal from '@/components/Modals/SearchModal/SearchModal.vue'
import FiltersModal from './FiltersModal/FiltersModal.vue'
import { useLocalizedRouter } from '@/composables/localizedRouter'
import { useMarketplaceStore } from '@/stores/ssr/useMarketplaceStore'
import { useCartStore } from '@/stores/useCartStore'

const { t } = useI18n()
const cartStore = useCartStore()
const marketplaceStore = useMarketplaceStore()
const router = useRouter()
const { localizedRoute } = useLocalizedRouter()

const state = reactive({
  searchModalOpen: false,
  filtersModalOpen: false,
})

const hasSearchQuery = computed(() => marketplaceStore.searchQuery !== null)

const activeFiltersAmount = computed(() => {
  const amount = marketplaceStore.filteredCategories?.length
  return amount > 9 ? '+' : amount
})

const basketItemsAmount = computed(() => cartStore.basketItems?.length)

async function gotoCheckout() {
  await router.push(localizedRoute({ name: 'checkout' }))
}

const route = useRoute()

const showBottomBar = computed<boolean>(() => !String(route.name).endsWith('slug'))
</script>

<style lang="scss" scoped>
@import "@/assets/css/mixins/styling.scss";
@import "@/assets/css/mixins/breakpoints-up.scss";

.bottom-bar {
  position:               fixed;
  bottom:                 0;
  left:                   0;
  width:                  100%;
  background-color:       #ffffff;
  z-index:                999;
  display:                flex;
  justify-content:        space-between;
  padding:                0.75rem 2rem calc(0.5rem + env(safe-area-inset-bottom));
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
  box-shadow:             0 -0.25rem 1rem 0rem rgba(0, 0, 0, 0.1);
  font-weight:            700;
  font-size:              0.875rem;

  @include lg-up {
    display: none;
  }

  &__item {
    position:    relative;
    cursor:      pointer;
    display:     flex;
    flex-direction: column;
    align-items: center;

    &:deep(.b-icon-icon svg) {
      width: 24px;
      height: 24px;
    }
  }

  &__notification {
    display:       flex;
    align-items:   center;
    justify-content: center;
    width:         1rem;
    height:        1rem;
    border-radius: 50%;
    position:      absolute;
    top:           -0.5rem;
    right:         0rem;
    @include gradient-button;
    font-size:     0.7rem;
    color:         #ffffff;
    line-height:   100%;
  }
}
</style>
